import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const ProfileSettings = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: 'John',
    lastName: 'Trader',
    email: 'john.trader@example.com',
    phone: '+1 (555) 123-4567',
    dateOfBirth: '1990-05-15',
    nationality: 'United States',
    address: '123 Trading Street',
    city: 'New York',
    state: 'NY',
    zipCode: '10001',
    country: 'United States',
    timezone: 'America/New_York',
    language: 'English',
    profilePhoto: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face'
  });
  const [tempData, setTempData] = useState({ ...profileData });
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const countries = [
    'United States', 'Canada', 'United Kingdom', 'Germany', 'France', 
    'Japan', 'Australia', 'Singapore', 'Switzerland', 'Netherlands'
  ];

  const timezones = [
    'America/New_York', 'America/Los_Angeles', 'America/Chicago', 'America/Denver',
    'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Asia/Tokyo', 'Asia/Singapore',
    'Australia/Sydney'
  ];

  const languages = [
    'English', 'Spanish', 'French', 'German', 'Japanese', 'Chinese', 'Korean', 'Portuguese'
  ];

  const handleInputChange = (field, value) => {
    setTempData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    setProfileData({ ...tempData });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempData({ ...profileData });
    setIsEditing(false);
  };

  const handlePhotoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      // Simulate upload delay
      setTimeout(() => {
        const reader = new FileReader();
        reader.onload = (e) => {
          const newData = { ...tempData, profilePhoto: e.target.result };
          setTempData(newData);
          if (!isEditing) {
            setProfileData(newData);
          }
          setIsUploading(false);
        };
        reader.readAsDataURL(file);
      }, 1500);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-heading-semibold text-text-primary mb-1">Profile Information</h2>
          <p className="text-sm text-text-secondary">Manage your personal information and contact details</p>
        </div>
        
        {!isEditing ? (
          <button
            onClick={() => setIsEditing(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
          >
            <Icon name="Edit" size={16} />
            <span className="font-body-medium">Edit Profile</span>
          </button>
        ) : (
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCancel}
              className="px-4 py-2 border border-border text-text-secondary rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-trading hover:bg-primary-600 transition-colors duration-trading-fast"
            >
              <Icon name="Save" size={16} />
              <span className="font-body-medium">Save Changes</span>
            </button>
          </div>
        )}
      </div>

      {/* Profile Photo Section */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-4">Profile Photo</h3>
        
        <div className="flex items-center space-x-6">
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-secondary-100">
              {isUploading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Loader2" size={24} className="text-primary animate-spin" />
                </div>
              ) : (
                <Image
                  src={tempData.profilePhoto}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="absolute -bottom-2 -right-2 w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors duration-trading-fast disabled:opacity-50"
            >
              <Icon name="Camera" size={16} />
            </button>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
          </div>
          
          <div>
            <h4 className="font-body-medium text-text-primary mb-1">Upload a new photo</h4>
            <p className="text-sm text-text-secondary mb-3">
              JPG, PNG or GIF. Max size 5MB. Recommended 400x400px.
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="text-sm text-primary hover:text-primary-600 font-body-medium disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Choose File'}
            </button>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Personal Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              First Name *
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.firstName}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Last Name *
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.lastName}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Email Address *
            </label>
            {isEditing ? (
              <input
                type="email"
                value={tempData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.email}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Phone Number
            </label>
            {isEditing ? (
              <input
                type="tel"
                value={tempData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.phone}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Date of Birth
            </label>
            {isEditing ? (
              <input
                type="date"
                value={tempData.dateOfBirth}
                onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{new Date(profileData.dateOfBirth).toLocaleDateString()}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Nationality
            </label>
            {isEditing ? (
              <select
                value={tempData.nationality}
                onChange={(e) => handleInputChange('nationality', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              >
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-text-primary">{profileData.nationality}</p>
            )}
          </div>
        </div>
      </div>

      {/* Address Information */}
      <div className="mb-8 p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Address Information</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Street Address
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.address}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              City
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.city}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              State/Province
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.state}
                onChange={(e) => handleInputChange('state', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.state}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              ZIP/Postal Code
            </label>
            {isEditing ? (
              <input
                type="text"
                value={tempData.zipCode}
                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              />
            ) : (
              <p className="py-2 text-text-primary">{profileData.zipCode}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Country
            </label>
            {isEditing ? (
              <select
                value={tempData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              >
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-text-primary">{profileData.country}</p>
            )}
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="p-6 border border-border rounded-trading">
        <h3 className="text-lg font-heading-semibold text-text-primary mb-6">Preferences</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Timezone
            </label>
            {isEditing ? (
              <select
                value={tempData.timezone}
                onChange={(e) => handleInputChange('timezone', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              >
                {timezones.map(timezone => (
                  <option key={timezone} value={timezone}>{timezone}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-text-primary">{profileData.timezone}</p>
            )}
          </div>
          
          <div>
            <label className="block text-sm font-body-medium text-text-primary mb-2">
              Language
            </label>
            {isEditing ? (
              <select
                value={tempData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary"
              >
                {languages.map(language => (
                  <option key={language} value={language}>{language}</option>
                ))}
              </select>
            ) : (
              <p className="py-2 text-text-primary">{profileData.language}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;